function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}
function queryDomNodes() {
  let selectors = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  let context = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document;
  const domNodes = Object.entries(selectors).reduce((acc, _ref) => {
    let [name, selector] = _ref;
    const findOne = typeof selector === 'string';
    const queryMethod = findOne ? 'querySelector' : 'querySelectorAll';
    const sl = findOne ? selector : selector[0];
    acc[name] = context?.[queryMethod]?.(sl);

    if (!findOne && acc[name]) {
      acc[name] = [...acc[name]];
    }

    return acc;
  }, {});
  return domNodes;
}
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
    this.querySelectorAll('button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach(submenu => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);
class FacetFiltersForm extends HTMLElement {

  constructor() {
    super();
   this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
 
    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state && typeof event.state.searchParams === 'string' ? event.state.searchParams : window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    }
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    const requestId = (FacetFiltersForm.requestId || 0) + 1;
    FacetFiltersForm.requestId = requestId;
    FacetFiltersForm.controller?.abort();
    const controller = new AbortController();
    FacetFiltersForm.controller = controller;
    document.getElementById('CollectionFilterError')?.remove();
    document.dispatchEvent(new CustomEvent('collection:loading'));
    const grid = document.getElementById('ProductGridContainer');
    grid?.setAttribute('aria-busy', 'true');
    const loader = document.getElementById('js-loading');
    if (loader) loader.style.display = 'block';

    try {
      const responses = await Promise.all(FacetFiltersForm.getSections().map(async (section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const cached = FacetFiltersForm.filterData.find(item => item.url === url);
        if (cached) return cached.html;
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) throw new Error('Unable to load filters');
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.getElementById('product-grid') || !parsed.getElementById('FacetsWrapperDesktop')) {
          throw new Error('Incomplete collection response');
        }
        if (requestId === FacetFiltersForm.requestId) {
          FacetFiltersForm.filterData.push({html, url});
          if (FacetFiltersForm.filterData.length > 20) FacetFiltersForm.filterData.shift();
        }
        return html;
      }));
      if (requestId !== FacetFiltersForm.requestId) return;
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
      FacetFiltersForm.searchParamsPrev = searchParams;
      responses.forEach(html => {
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      });
      window.currentPage = 1;
    } catch (error) {
      if (requestId !== FacetFiltersForm.requestId || error.name === 'AbortError') return;
      const message = document.createElement('div');
      message.id = 'CollectionFilterError';
      message.setAttribute('role', 'alert');
      message.className = 'Container';
      const description = document.createElement('p');
      description.textContent = 'Filters could not be updated. The previous products are still shown.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'Button Button--secondary';
      retry.textContent = 'Retry filters';
      retry.addEventListener('click', () => FacetFiltersForm.renderPage(searchParams, null, updateURLHash));
      message.append(description, retry);
      const drawer = document.getElementById('collection-filter-drawer');
      const host = drawer?.getAttribute('aria-hidden') === 'false'
        ? drawer.querySelector('.Drawer__Main') : document.getElementById('product-grid');
      host?.prepend(message);
      document.dispatchEvent(new CustomEvent('collection:updated'));
    } finally {
      if (requestId === FacetFiltersForm.requestId) {
        if (loader) loader.style.display = 'none';
        grid?.removeAttribute('aria-busy');
        grid?.querySelector('.collection')?.classList.remove('loading');
        document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach(el => el.classList.remove('loading'));
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const nextProducts = parsedHTML.getElementById('product-grid');
    const products = document.getElementById('product-grid');
    if (!nextProducts || !products) return;
    products.innerHTML = nextProducts.innerHTML;
    const loader = document.getElementById('js-loading');
    if (loader) loader.style.display = 'none';
    document.dispatchEvent(new CustomEvent('collection:updated'));
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount')?.innerHTML
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
  }

  static renderFilters(html, event) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.getElementById('FacetsWrapperDesktop');
    const target = document.getElementById('FacetsWrapperDesktop');
    if (!source || !target) return;
    const key = group => group.querySelector('input[name]')?.name || group.querySelector('fieldset')?.getAttribute('aria-label');
    const states = new Map(Array.from(target.querySelectorAll('.js-filter')).map(group => [key(group), group.open]));
    const focused = target.contains(document.activeElement) ? document.activeElement : null;
    const focusName = focused?.getAttribute('name');
    const focusValue = focused?.getAttribute('value');
    source.querySelectorAll('.js-filter').forEach(group => {
      if (states.has(key(group))) group.open = states.get(key(group));
    });
    target.innerHTML = source.innerHTML;
    if (focusName) {
      Array.from(target.querySelectorAll('input[name]')).find(input => input.name === focusName && input.value === focusValue)?.focus({preventScroll: true});
    }
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    })

    FacetFiltersForm.toggleActiveFacets(false);
  }


  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');
    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }

    if (targetElementAccessibility && sourceElementAccessibility) {
      target.querySelector('.facets__summary').outerHTML = source.querySelector('.facets__summary').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }
    ]
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
   
      const forms = [];
     

      sortFilterForms.forEach((form) => {
          if (form.id === 'FacetFiltersForm') {
            const noJsElements = document.querySelectorAll('.no-js-list');
            noJsElements.forEach((el) => el.remove());
            forms.push(this.createSearchParams(form));
          } 
      });
      this.onSubmitForm(forms.join('&'), event)
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class PriceRange extends HTMLElement {
  constructor() {
    super();

    _defineProperty(this, "selectors", {
      inputs: ['input[type="number"]'],
      ranges: ['input[type="range"]'],
      minRange: '[data-type="min-range"]',
      maxRange: '[data-type="max-range"]',
      minInput: '[data-type="min-input"]',
      maxInput: '[data-type="max-input"]'
    });

    this.domNodes = queryDomNodes(this.selectors, this);
    this.priceGap = parseInt(this.dataset.priceGap);
    this.priceMax = parseInt(this.dataset.priceMax);
    this.domNodes.inputs.forEach(el => el.addEventListener('change', this.onInputChange.bind(this)));
    this.domNodes.ranges.forEach(el => el.addEventListener('input', this.onSliderChange.bind(this)));
    this.setMinAndMaxValues();
  }

  onSliderChange(event) {
    const input = event.currentTarget;
    const type = input.dataset.type;
    const value = Number(input.value);
    console.log(input, type, value, 'onSliderChange');
    const {
      minInput,
      maxInput,
      minRange,
      maxRange
    } = this.domNodes;
    if (type === 'min-range') {
      
      if (maxRange.value - input.value >= this.priceGap) {
        minInput.value = input.value +".00";
      } else {
        console.log('else');
        input.value = maxRange.value - this.priceGap;
      }
    }

    if (type === 'max-range') {
      if (input.value - minRange.value >= this.priceGap) {
        maxInput.value = input.value +".00";
      } else {
        input.value = Number(minRange.value) + Number(this.priceGap);
      }
    }

    this.style.setProperty('--from', 100 * minRange.value / this.priceMax + '%');
    this.style.setProperty('--to', 100 * maxRange.value / this.priceMax + '%');
  }

  onInputChange(event) {
    this.adjustToValidValues(event.target);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const {
      minInput,
      maxInput
    } = this.domNodes;
    if (maxInput.value) minInput?.setAttribute('max', Number(maxInput.value) - this.priceGap);
    if (minInput.value) maxInput?.setAttribute('min', Number(minInput.value) + this.priceGap);
    if (minInput.value === '') maxInput?.setAttribute('min', 0);
    if (maxInput.value === '') minInput?.setAttribute('max', maxInput.getAttribute('max'));



    
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (value < min) input.value = min +".00";
    if (value > max) input.value = max +".00";


  }

}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

function closeFunction() {
  var closeElement = document.querySelector(".js-sidebar");
  closeElement.classList.remove("active");
  var overlayElement = document.querySelector(".js-toggle-filter");
  overlayElement.classList.remove("active");
}
function applyFunction() {
  var applyElement = document.querySelector(".js-sidebar");
  applyElement.classList.remove("active");
  var overlayElement = document.querySelector(".js-toggle-filter");
  overlayElement.classList.remove("active");
}






    document.addEventListener('DOMContentLoaded', function() {
      const sidebar = document.querySelector('.CollectionInner__Sidebar .facets-container');
      const drawer = document.querySelector('#collection-filter-drawer .Drawer__Main');
      if (!sidebar || !drawer) return;
      const desktopParent = sidebar.parentElement;
      const desktopAnchor = document.createComment('Collection filter position');
      desktopParent.insertBefore(desktopAnchor, sidebar);
      const mobile = window.matchMedia('(max-width: 1007px)');
      function moveFilters() {
        if (mobile.matches) {
          drawer.appendChild(sidebar);
        } else {
          const drawerPanel = drawer.closest('.Drawer');
          if (drawerPanel?.getAttribute('aria-hidden') === 'false') {
            drawerPanel.querySelector('[data-action="close-drawer"]')?.click();
          }
          desktopAnchor.after(sidebar);
        }
      }
      moveFilters();
      mobile.addEventListener('change', moveFilters);
    });

    document.addEventListener('click', function(event) {
      const button = event.target.closest('.button-show-more');
      if (!button) return;
      event.preventDefault();
      const panel = button.closest('.parent-display');
      panel?.querySelectorAll('.show-more-item').forEach(item => item.classList.toggle('hidden'));
      button.querySelectorAll('span').forEach(span => span.classList.toggle('hidden'));
    });
